import {
  JOB_STAGES,
  JOB_STAGE_TRANSITIONS,
  type JobStage,
} from "@sts/schemas";
import type { JobStageTag } from "@sts/doc-catalogue";

/**
 * Pipeline vocabulary, taken from `@sts/schemas` rather than restated.
 *
 * The stage list and the transition table are imported, not copied: a portal
 * that disagrees with the API about which moves are legal would offer buttons
 * that always 409.
 */

export const STAGE_LABEL: Record<JobStage, string> = {
  lead: "Lead",
  qualified: "Qualified",
  "estimate-sent": "Estimate sent",
  "survey-booked": "Survey booked",
  surveyed: "Surveyed",
  designed: "Designed",
  "proposal-sent": "Proposal sent",
  signed: "Signed",
  scheduled: "Scheduled",
  installed: "Installed",
  commissioned: "Commissioned",
  certified: "Certified",
  "bus-redeemed": "BUS redeemed",
  "handover-complete": "Handover complete",
  lost: "Lost",
  cancelled: "Cancelled",
};

/**
 * The order the pipeline is read in.
 *
 * `JOB_STAGES` comes out of `Object.keys` on the transition table, which is
 * already declaration order — but relying on key order for something a person
 * reads left to right is the kind of assumption that quietly breaks. The
 * closed-out stages are pushed to the end because a board that opens with
 * "Lost" is not a board anybody wants.
 */
const CLOSED: readonly JobStage[] = ["lost", "cancelled", "handover-complete"];

export const PIPELINE_ORDER: readonly JobStage[] = [
  ...JOB_STAGES.filter((stage) => !CLOSED.includes(stage)),
  ...CLOSED,
];

export function isClosed(stage: JobStage): boolean {
  return CLOSED.includes(stage);
}

/** Where a job at this stage can go next, straight from the API's own table. */
export function nextStages(stage: JobStage): readonly JobStage[] {
  return JOB_STAGE_TRANSITIONS[stage] ?? [];
}

/**
 * The extra field the API requires for a given move.
 *
 * Both of these start compliance clocks — the 30-day MCS certificate and the
 * 120-day BUS window from commissioning, the 28-day DNO notification from
 * installation — so the API refuses the transition without them. Asking for the
 * date in the form is better than letting the user find out via a 400.
 */
export function requiredDateField(
  to: JobStage,
): { field: "commissioningDate" | "installationDate"; label: string } | null {
  if (to === "commissioned") {
    return {
      field: "commissioningDate",
      label: "Commissioning date",
    };
  }
  if (to === "installed") {
    return { field: "installationDate", label: "Installation date" };
  }
  return null;
}

/**
 * Which part of the document catalogue is relevant at this stage.
 *
 * The catalogue tags documents with its own coarser vocabulary
 * (`JobStageTag`), so this is the join between the two. A closed job gets
 * nothing rather than a misleading suggestion.
 */
export function documentStageFor(stage: JobStage): JobStageTag | null {
  switch (stage) {
    case "lead":
    case "qualified":
      return "enquiry";
    case "estimate-sent":
    case "proposal-sent":
      return "quote";
    case "survey-booked":
    case "surveyed":
      return "survey";
    case "designed":
      return "design";
    case "signed":
      return "contract";
    case "scheduled":
    case "installed":
      return "install";
    case "commissioned":
      return "commission";
    case "certified":
    case "bus-redeemed":
    case "handover-complete":
      return "handover";
    case "lost":
    case "cancelled":
      return null;
  }
}
